import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import {
  Button,
  Flex,
  FormLabel,
  Heading,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Text,
} from "@chakra-ui/react";
import { Form, Formik, FormikHelpers } from "formik";
import { InputControl, SelectControl } from "formik-chakra-ui";
import React, { useCallback, useEffect, useState } from "react";
import { MultiSelectInput } from "src/components/Inputs/MultiSelectInput";
import { ResizableDrawer } from "src/components/Layout/ResizableDrawer";
import {
  createOfferConfiguration,
  OfferConfigurationValidationResult,
  updateOfferConfiguration,
} from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import {
  generateKey,
  isUserGeneratedKey,
} from "src/scenes/orgAdmin/formTemplates/ContentTab/V2/autoGenerateKey";
import { validateWithZod } from "src/services/formValidations";
import { PASSWORD_MANAGER_IGNORE } from "src/types/passwordManager";
import { z } from "zod";
import { OfferExpirationConfiguration } from "./offerConfigurationTypes";
import { TimePicker } from "./TimePicker";

interface School {
  id: string;
  name: string;
}

export interface OfferConfigurationFormData {
  name: string;
  description: string;
  key: string;
  // Tracks whether the user manually edited the key so we stop auto-generating
  // it from the name. Not surfaced to the user.
  userGeneratedKey: boolean;
  expiration_days: number;
  expiration_day_type: "CALENDAR" | "WEEKDAY";
  expiration_time: Date | null;
  school_ids: string[];
  form_template_id: string;
}

const getDefault5PM = (): Date => {
  const date = new Date();
  date.setHours(17, 0, 0, 0);
  return date;
};

const defaultValues: OfferConfigurationFormData = {
  name: "",
  description: "",
  key: "",
  userGeneratedKey: false,
  expiration_days: 1,
  expiration_day_type: "CALENDAR",
  expiration_time: getDefault5PM(),
  school_ids: [],
  form_template_id: "",
};

const FormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string(),
  key: z.string().min(1, "A rule key is required"),
  userGeneratedKey: z.boolean(),
  expiration_days: z
    .number()
    .int()
    .min(1, "Expiration days must be 1 or greater"),
  expiration_day_type: z.enum(["CALENDAR", "WEEKDAY"]),
  expiration_time: z
    .date({ required_error: "Time is required" })
    .nullable()
    .refine((date) => date !== null, "Time is required")
    .refine((date) => {
      if (date === null) return false;
      const minutes = date.getMinutes();
      return minutes === 0 || minutes === 30;
    }, "Time must be on the hour or half hour (00 or 30 minutes)"),
  school_ids: z.array(z.string()),
  form_template_id: z.string(),
});

export interface Props {
  configuration: OfferExpirationConfiguration | null;
  formTemplateId: string;
  schools: School[];
  isOpen: boolean;
  onClose: () => void;
  refetch: () => Promise<void>;
}

const componentId = "offer-configuration-form";

const convert24HourToDate = (time24: string): Date => {
  const parts = time24.split(":");
  const hours = parseInt(parts[0] ?? "0", 10);
  const minutes = parseInt(parts[1] ?? "0", 10);
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
};

const convertDateTo24Hour = (date: Date | null): string => {
  if (!date) return "00:00:00";
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}:00`;
};

const toFormData = (
  config: OfferExpirationConfiguration
): OfferConfigurationFormData => {
  const { configuration } = config;
  const existingKey = config.key ?? "";
  const ruleKey =
    existingKey.trim().length > 0 ? existingKey : generateKey(config.name);

  return {
    name: config.name,
    description: config.description || "",
    key: ruleKey,
    userGeneratedKey: isUserGeneratedKey(ruleKey, config.name),
    expiration_days: configuration.expiration_days,
    expiration_day_type: configuration.expiration_day_type ?? "CALENDAR",
    expiration_time: convert24HourToDate(configuration.expiration_time),
    school_ids: configuration.school_ids || [],
    form_template_id: config.form_template_id,
  };
};

export const OfferConfigurationDrawer: React.FC<Props> = ({
  configuration,
  formTemplateId,
  schools,
  isOpen,
  onClose,
  refetch,
}) => {
  const toast = useAvelaToast();
  const [validationResults, setValidationResults] = useState<
    OfferConfigurationValidationResult[]
  >([]);

  const isEditMode = !!configuration;

  const schoolErrorById = new Map(
    validationResults.flatMap((result) =>
      result.level === "School"
        ? [[result.schoolId, result.error] as const]
        : []
    )
  );

  const keyError = validationResults.find(
    (result) =>
      result.level === "Configuration" &&
      "fieldName" in result &&
      result.fieldName === "key"
  )?.error;

  const generalErrors = validationResults
    .filter((result) => result.level !== "School")
    .filter((result) => !("fieldName" in result && result.fieldName === "key"))
    .map((result) => result.error);

  useEffect(() => {
    if (isOpen) {
      setValidationResults([]);
    }
  }, [isOpen, configuration?.id]);

  const handleClose = useCallback(() => {
    setValidationResults([]);
    onClose();
  }, [onClose]);

  const submitForm = useCallback(
    async (
      values: OfferConfigurationFormData,
      formikHelpers: FormikHelpers<OfferConfigurationFormData>
    ) => {
      try {
        setValidationResults([]);

        const configurationPayload = {
          expiration_days: values.expiration_days,
          expiration_day_type: values.expiration_day_type,
          expiration_time: convertDateTo24Hour(values.expiration_time),
          school_ids: values.school_ids,
        };

        const response = configuration
          ? await updateOfferConfiguration({
              path: { id: configuration.id },
              body: {
                name: values.name,
                description: values.description || undefined,
                key: values.key,
                configuration: configurationPayload,
              },
            })
          : await createOfferConfiguration({
              body: {
                name: values.name,
                description: values.description || undefined,
                key: values.key,
                form_template_id: values.form_template_id,
                active: false,
                configuration: configurationPayload,
                configuration_type: "OFFER_EXPIRATION",
              },
            });

        const outcome = response.data?.data;
        if (outcome?.result === "invalid") {
          setValidationResults(outcome.validationResults);
          return;
        }

        toast({
          title: "Success",
          description: isEditMode ? "Rule updated" : "Rule created",
          status: "success",
        });

        await refetch();
        handleClose();
      } catch (error) {
        console.error(error);
        toast.error({
          title: `Failed to ${isEditMode ? "update" : "create"} rule`,
          description: `An error occurred while ${
            isEditMode ? "updating" : "creating"
          } the rule.`,
        });
      } finally {
        formikHelpers.setSubmitting(false);
      }
    },
    [configuration, isEditMode, toast, refetch, handleClose]
  );

  const headerContent = (
    <Heading as="h3" size="1.25rem">
      {isEditMode ? "Edit rule" : "Create rule"}
    </Heading>
  );

  return (
    <Formik<OfferConfigurationFormData>
      key={configuration?.id ?? `new-${isOpen}`}
      initialValues={
        configuration
          ? toFormData(configuration)
          : { ...defaultValues, form_template_id: formTemplateId }
      }
      validate={validateWithZod(FormSchema)}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={async (values, formikHelpers) => {
        await submitForm(values, formikHelpers);
      }}
    >
      {(formik) => (
        <ResizableDrawer
          isOpen={isOpen}
          placement="right"
          onClose={handleClose}
          size="lg"
          header={headerContent}
          footer={
            <>
              <Button
                variant="outline"
                mr={3}
                onClick={handleClose}
                width="50%"
                colorScheme="gray"
              >
                Cancel
              </Button>
              <Button
                colorScheme="primary"
                width="50%"
                type="submit"
                form={componentId}
                isLoading={formik.isSubmitting}
              >
                Save
              </Button>
            </>
          }
          autosaveId="offer-configuration-drawer-width"
        >
          <Form id={componentId} noValidate>
            <Flex gap="1.5rem" pb="1.5rem" direction="column">
              <Flex direction="column" gap="0.625rem">
                <FormLabel p="0" m="0" color={"gray.700"}>
                  Rule name
                </FormLabel>
                <InputControl
                  name="name"
                  isRequired={true}
                  inputProps={{
                    placeholder: "E.g.: Auto revoke",
                    autoComplete: "off",
                    ...PASSWORD_MANAGER_IGNORE,
                    onChange: (event) => {
                      const name = event.target.value;
                      void formik.setFieldValue("name", name);
                      if (!formik.values.userGeneratedKey) {
                        void formik.setFieldValue("key", generateKey(name));
                      }
                    },
                  }}
                />
              </Flex>
              <Flex direction="column" gap="0.625rem">
                <FormLabel p="0" m="0" color={"gray.700"}>
                  Rule key
                </FormLabel>
                <InputControl
                  name="key"
                  isRequired={true}
                  inputProps={{
                    placeholder: "E.g.: auto-revoke",
                    autoComplete: "off",
                    ...PASSWORD_MANAGER_IGNORE,
                    onChange: (event) => {
                      const key = event.target.value;
                      void formik.setFieldValue("key", key);
                      void formik.setFieldValue(
                        "userGeneratedKey",
                        key.trim().length > 0
                      );
                    },
                  }}
                />
                {keyError && (
                  <Text fontSize="sm" color="red.500">
                    {keyError}
                  </Text>
                )}
              </Flex>
              <Flex direction="column" gap="0.625rem">
                <FormLabel p="0" m="0" color={"gray.700"}>
                  Rule description (optional)
                </FormLabel>
                <InputControl
                  name="description"
                  inputProps={{
                    placeholder:
                      "E.g.: Automatically revoke offers after expiration",
                    autoComplete: "off",
                    ...PASSWORD_MANAGER_IGNORE,
                  }}
                />
              </Flex>
            </Flex>

            <Flex
              direction="column"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="md"
            >
              <Flex direction="column" gap="0.5rem" p="1rem">
                <Heading as="h4" size="sm">
                  What would you like to do?
                </Heading>
                <Flex direction="column">
                  <Text fontWeight="semibold" fontSize="sm">
                    Revoke offers
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    Given a certain condition, automatically revoke offers
                  </Text>
                </Flex>
              </Flex>

              <Flex
                direction="column"
                gap="0.5rem"
                p="1rem"
                borderTop="1px solid"
                borderTopColor="gray.200"
              >
                <Heading as="h4" size="sm">
                  When would you like to revoke offers?
                </Heading>

                <Flex direction="column" gap="0.625rem">
                  <FormLabel p="0" m="0" color={"gray.700"}>
                    Which schools should this rule apply to?
                  </FormLabel>
                  <MultiSelectInput
                    size="md"
                    options={schools
                      .map((school) => ({
                        id: school.id,
                        label: school.name,
                      }))
                      .sort((a, b) => a.label.localeCompare(b.label))}
                    initialValues={formik.values.school_ids.map((schoolId) => {
                      const school = schools.find((s) => s.id === schoolId);
                      return {
                        id: schoolId,
                        label: school?.name || schoolId,
                      };
                    })}
                    onChange={(selectedSchools) => {
                      formik.setFieldValue(
                        "school_ids",
                        selectedSchools.map((s) => s.id)
                      );
                    }}
                    placeholder="Select multiple"
                    error={
                      schoolErrorById.size > 0
                        ? "Some selected schools are already used by another revoke rule."
                        : undefined
                    }
                    isInvalidItem={(item) => schoolErrorById.has(item.id)}
                    getInvalidItemMessage={(item) =>
                      schoolErrorById.get(item.id)
                    }
                    renderDropdownItem={(item) => (
                      <Flex key={item.id}>{item.label}</Flex>
                    )}
                  />
                  {generalErrors.length > 0 && (
                    <Flex direction="column" gap="0.25rem">
                      {generalErrors.map((message, index) => (
                        <Text
                          key={`general-error-${index}`}
                          fontSize="sm"
                          color="red.500"
                        >
                          {message}
                        </Text>
                      ))}
                    </Flex>
                  )}
                  <Text fontSize="sm" color="gray.600">
                    The rule will apply only to the selected schools. If no
                    schools are selected, it will apply to all schools.
                  </Text>
                </Flex>

                <Flex direction="column" gap="0.625rem">
                  <FormLabel p="0" m="0" color={"gray.700"}>
                    How many days should the offer be valid for?
                  </FormLabel>
                  <Flex direction="row" gap="0.5rem" alignItems="center">
                    <NumberInput
                      min={0}
                      value={
                        isNaN(formik.values.expiration_days)
                          ? 0
                          : formik.values.expiration_days
                      }
                      onChange={(_valueString, valueNumber) => {
                        formik.setFieldValue(
                          "expiration_days",
                          isNaN(valueNumber) ? 0 : valueNumber
                        );
                      }}
                      flex="1"
                    >
                      <NumberInputField />
                      <NumberInputStepper border="none">
                        <NumberIncrementStepper
                          border="none"
                          paddingRight="0.5rem"
                          children={
                            <ChevronUpIcon color="gray.500" fontSize={"1rem"} />
                          }
                        />
                        <NumberDecrementStepper
                          border="none"
                          paddingRight="0.5rem"
                          children={
                            <ChevronDownIcon
                              color="gray.500"
                              fontSize={"1rem"}
                            />
                          }
                        />
                      </NumberInputStepper>
                    </NumberInput>
                    <SelectControl name="expiration_day_type" flex="1">
                      <option value="CALENDAR">Days</option>
                      <option value="WEEKDAY">Weekdays</option>
                    </SelectControl>
                  </Flex>
                  {formik.errors.expiration_days && (
                    <Text fontSize="sm" color="red.500">
                      {formik.errors.expiration_days}
                    </Text>
                  )}
                  <Text fontSize="sm" color="gray.600">
                    Select the number of consecutive days any new offer is valid
                    for. Preexisting offers will not be impacted.
                  </Text>
                </Flex>

                <Flex direction="column" gap="0.625rem">
                  <FormLabel p="0" m="0" color={"gray.700"}>
                    At what time should the offer be revoked?
                  </FormLabel>
                  <TimePicker
                    value={formik.values.expiration_time}
                    onChange={(date) =>
                      formik.setFieldValue("expiration_time", date)
                    }
                  />
                  {formik.errors.expiration_time && (
                    <Text fontSize="sm" color="red.500">
                      {formik.errors.expiration_time}
                    </Text>
                  )}
                  <Text fontSize="sm" color="gray.600">
                    Indicate the time of day at which the offer should be
                    revoked
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          </Form>
        </ResizableDrawer>
      )}
    </Formik>
  );
};
